import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { useSessionUser } from "@/hooks/useSessionUser";
import { cn } from "@/lib/utils";

type ReviewerOption = {
  id: string;
  label: string;
};

export interface HitlAssignMenuProps {
  assignedLabel?: string | null;
  onAssign: (userId: string | null) => Promise<void> | void;
  busy?: boolean;
  reviewers?: ReviewerOption[];
  className?: string;
  disabled?: boolean;
}

export function HitlAssignMenu({
  assignedLabel,
  onAssign,
  busy = false,
  reviewers = [],
  className,
  disabled = false,
}: HitlAssignMenuProps) {
  const { user } = useSessionUser();
  const [open, setOpen] = useState(false);
  const [localBusy, setLocalBusy] = useState(false);

  const reviewerOptions = useMemo(() => {
    const list: ReviewerOption[] = [];
    if (user?.id) {
      list.push({
        id: String(user.id),
        label: user.name ?? user.email ?? "Assign to me",
      });
    }
    reviewers.forEach((option) => {
      if (!list.some((existing) => existing.id === option.id)) {
        list.push(option);
      }
    });
    return list;
  }, [reviewers, user?.email, user?.id, user?.name]);

  const handleAssign = async (userId: string | null) => {
    setLocalBusy(true);
    try {
      await onAssign(userId);
    } finally {
      setLocalBusy(false);
      setOpen(false);
    }
  };

  return (
    <div className={cn("relative inline-flex", className)}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setOpen((prev) => !prev)}
        disabled={busy || localBusy || disabled}
        className="rounded-full border border-border text-xs uppercase tracking-wide"
      >
        {assignedLabel ? `Assigned: ${assignedLabel}` : "Assign reviewer"}
      </Button>
      {open && (
        <div className="absolute right-0 z-30 mt-2 w-48 rounded-2xl border border-border bg-background/95 p-2 text-sm shadow-2xl">
          <p className="px-2 pb-2 text-xs uppercase tracking-wide text-muted-foreground">Assign reviewer</p>
          <div className="space-y-1">
            {reviewerOptions.map((option) => (
              <button
                type="button"
                key={option.id}
                className="w-full rounded-xl px-3 py-2 text-left text-foreground/80 transition hover:bg-background/40"
                onClick={() => handleAssign(option.id)}
                disabled={busy || localBusy || disabled}
              >
                {option.label}
              </button>
            ))}
            <button
              type="button"
              className="w-full rounded-xl px-3 py-2 text-left text-muted-foreground transition hover:bg-background/40"
              onClick={() => handleAssign(null)}
              disabled={busy || localBusy || disabled}
            >
              Clear assignment
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default HitlAssignMenu;
